import clsx from "clsx";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Order, OrderChange, OrderFieldSpec, OrderFieldSpecs, UnitMission } from "../../api/types";
import { attentionSelector, designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Select } from "../../components/Select";
import { Badge, Button, IconButton, inputClass, textareaClass } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { beforeAfter, changedFields, changesByTab } from "./changes";
import { outdatedByTab } from "./outdated";
import { SOURCE_LABELS } from "./rotationSteps";
import { type OrderSaveQueue, registerDrain } from "./orderSaveQueue";
import { useDossierAccess } from "../../sharing/access";
import { readOnlyText } from "../../sharing/ReadOnlyBanner";
import { useUi } from "../../state/ui";

/** Réglage Météo du dossier (Données initiales), ouvert et désigné depuis le paragraphe Météo de l'ordre. */
function openWeatherSetting() {
  useUi.getState().setView("mission");
  designateSoon(attentionSelector("dossier-weather"));
}

/** Champ de l'aperçu → champ de l'éditeur (le PDF imprime l'ennemi initial en trois paragraphes). */
export function editorKey(field: string): string {
  if (field === "enemyInitialCombined") return "enemyInitialText";
  if (field.startsWith("brigadeUnitMissions") || field.startsWith("sgtiaUnitMissions")) return field.split(":")[0] ?? field;
  if (field === "enemyME1DefenseText" || field === "enemyME2DefenseText") return field;
  return field;
}

function AutoTextarea({
  value,
  onChange,
  onBlur,
  disabled,
  id,
  minHeight = 64,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled: boolean;
  id: string;
  minHeight?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeight), 520)}px`;
  }, [value, minHeight]);
  return (
    <textarea
      id={id}
      ref={ref}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      spellCheck
      lang="fr"
      // Hauteur automatique : pas de poignée de redimensionnement, qu'écraserait la frappe suivante.
      className={clsx(textareaClass, "resize-none")}
    />
  );
}

function MissionsEditor({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: UnitMission[];
  onChange: (v: UnitMission[]) => void;
  onBlur: () => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-3">
      {value.map((m, i) => (
        <div key={m.unitName || i} className="rounded-lg border border-line p-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="truncate text-ui font-semibold text-ink">- {m.displayName}</span>
            <Checkbox
              className="shrink-0"
              checked={m.isEffort}
              disabled={disabled}
              label={t("Effort")}
              onChange={(checked) => {
                onChange(value.map((x, j) => ({ ...x, isEffort: j === i ? checked : false })));
                onBlur();
              }}
            />
          </div>
          <AutoTextarea
            id={`mission-${i}`}
            value={m.mission || m.missionT1}
            disabled={disabled}
            onChange={(v) => onChange(value.map((x, j) => (j === i ? { ...x, mission: v } : x)))}
            onBlur={onBlur}
          />
        </div>
      ))}
    </div>
  );
}

interface MatrixColumn {
  temps: number;
  title: string;
  gdhWindow: string;
  isEffort: boolean;
}

interface MatrixRow {
  displayName: string;
  isEffort: boolean;
  cells: string[];
}

interface Matrix {
  columns: MatrixColumn[];
  rows: MatrixRow[];
  emd: string;
}

function parseMatrix(text: unknown): Matrix | null {
  if (typeof text !== "string" || !text) return null;
  try {
    const value = JSON.parse(text) as Partial<Matrix>;
    if (!Array.isArray(value.columns) || !Array.isArray(value.rows) || !value.columns.length) return null;
    return { columns: value.columns, rows: value.rows, emd: String(value.emd ?? "") };
  } catch {
    return null;
  }
}

/** Matrice du §3.b de la division (brigades × temps, puis EMD), telle qu'imprimée. */
function MatrixEditor({
  id,
  value,
  onChange,
  onBlur,
  disabled,
}: {
  id: string;
  value: unknown;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled: boolean;
}) {
  const matrix = parseMatrix(value);
  if (!matrix) {
    return <p className="px-1 text-ui text-ink-muted">{t("Matrice indisponible : régénérez l'ordre.")}</p>;
  }
  const update = (next: Matrix) => onChange(JSON.stringify(next));
  const setCell = (row: number, col: number, text: string) =>
    update({
      ...matrix,
      rows: matrix.rows.map((r, i) =>
        i === row ? { ...r, cells: r.cells.map((c, j) => (j === col ? text : c)) } : r,
      ),
    });
  return (
    <div id={id} className="space-y-3">
      {matrix.rows.map((row, i) => (
        <div key={row.displayName || i} className="rounded-lg border border-line p-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="truncate text-ui font-semibold text-ink">- {row.displayName}</span>
            <Checkbox
              className="shrink-0"
              checked={row.isEffort}
              disabled={disabled}
              label={t("Effort")}
              onChange={(checked) => {
                update({
                  ...matrix,
                  rows: matrix.rows.map((r, j) => ({ ...r, isEffort: j === i ? checked : false })),
                });
                onBlur();
              }}
            />
          </div>
          <div className="space-y-1.5">
            {matrix.columns.map((col, j) => (
              <div key={col.temps ?? j}>
                <label
                  htmlFor={`${id}-${i}-${j}`}
                  className={clsx("mb-0.5 block truncate text-meta", col.isEffort ? "font-medium text-ink" : "text-ink-muted")}
                  title={col.gdhWindow}
                >
                  {t("Temps {n} — {title}", { n: col.temps, title: col.title })}
                  {col.isEffort ? ` (${t("Effort")})` : ""}
                </label>
                <AutoTextarea
                  id={`${id}-${i}-${j}`}
                  value={row.cells[j] ?? ""}
                  disabled={disabled}
                  minHeight={34}
                  onChange={(text) => setCell(i, j, text)}
                  onBlur={onBlur}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div>
        <label htmlFor={`${id}-emd`} className="mb-0.5 block text-meta text-ink-muted">
          {t("EMD")}
        </label>
        <AutoTextarea
          id={`${id}-emd`}
          value={matrix.emd}
          disabled={disabled}
          minHeight={34}
          onChange={(text) => update({ ...matrix, emd: text })}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
}

interface AnnexTable {
  columns: string[];
  rows: { label: string; cells: string[] }[];
}

function parseTable(text: unknown): AnnexTable | null {
  if (typeof text !== "string" || !text) return null;
  try {
    const value = JSON.parse(text) as Partial<AnnexTable>;
    if (!Array.isArray(value.columns) || !value.columns.length || !Array.isArray(value.rows)) return null;
    return {
      columns: value.columns.map((c) => String(c ?? "")),
      rows: value.rows.map((r) => ({ label: String(r?.label ?? ""), cells: (r?.cells ?? []).map((c) => String(c ?? "")) })),
    };
  } catch {
    return null;
  }
}

/** Tableau d'une annexe de la division (mission feux par temps, DLOC, estimations de pertes…) : une carte par
 *  ligne, une case par colonne, sous l'intitulé de sa colonne. Libellés et en-têtes restent ceux de l'ordre. */
function TableEditor({
  id,
  value,
  onChange,
  onBlur,
  disabled,
}: {
  id: string;
  value: unknown;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled: boolean;
}) {
  const table = parseTable(value);
  if (!table) {
    return <p className="px-1 text-ui text-ink-muted">{t("Tableau indisponible : régénérez l'ordre.")}</p>;
  }
  const headers = table.columns.slice(1).map((c) => c.replace(/\n/g, " "));
  const setCell = (row: number, col: number, text: string) =>
    onChange(
      JSON.stringify({
        ...table,
        rows: table.rows.map((r, i) => (i === row ? { ...r, cells: r.cells.map((c, j) => (j === col ? text : c)) } : r)),
      }),
    );
  return (
    <div id={id} className="space-y-3">
      {table.rows.map((row, i) => (
        <div key={`${row.label}-${i}`} className="rounded-lg border border-line p-2.5">
          <p className="mb-1.5 truncate text-ui font-semibold text-ink">{row.label}</p>
          <div className="space-y-1.5">
            {headers.map((header, j) => (
              <div key={`${header}-${j}`}>
                <label htmlFor={`${id}-${i}-${j}`} className="mb-0.5 block truncate text-meta text-ink-muted" title={header}>
                  {header}
                </label>
                <AutoTextarea
                  id={`${id}-${i}-${j}`}
                  value={row.cells[j] ?? ""}
                  disabled={disabled}
                  minHeight={34}
                  onChange={(text) => setCell(i, j, text)}
                  onBlur={onBlur}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ArticulationRow {
  name: string;
  effort: boolean;
  lines: string[];
}

/** « nom\tE|-\tligne1␞ligne2 », une grande unité par ligne (format de la composition et de l'iPad). */
function parseArticulation(text: unknown): ArticulationRow[] {
  if (typeof text !== "string" || !text) return [];
  return text
    .split("\n")
    .map((row) => row.split("\t"))
    .filter((parts) => parts.length >= 3)
    .map(([name, effort, rest]) => ({ name: name ?? "", effort: effort === "E", lines: (rest ?? "").split("\x1e") }));
}

function serializeArticulation(rows: ArticulationRow[]): string {
  return rows.map((r) => `${r.name}\t${r.effort ? "E" : "-"}\t${r.lines.join("\x1e")}`).join("\n");
}

/** Articulation de la division : renforcements et prélèvements de chaque grande unité, une ligne par mesure. */
function ArticulationEditor({
  id,
  value,
  onChange,
  onBlur,
  disabled,
}: {
  id: string;
  value: unknown;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled: boolean;
}) {
  const rows = parseArticulation(value);
  if (!rows.length) {
    return <p className="px-1 text-ui text-ink-muted">{t("Articulation indisponible : régénérez l'ordre.")}</p>;
  }
  const update = (next: ArticulationRow[]) => onChange(serializeArticulation(next));
  return (
    <div id={id} className="space-y-3">
      {rows.map((row, i) => (
        <div key={row.name || i} className="rounded-lg border border-line p-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="truncate text-ui font-semibold text-ink">- {row.name}</span>
            <Checkbox
              className="shrink-0"
              checked={row.effort}
              disabled={disabled}
              label={t("Effort")}
              onChange={(checked) => {
                update(rows.map((r, j) => ({ ...r, effort: j === i ? checked : false })));
                onBlur();
              }}
            />
          </div>
          <AutoTextarea
            id={`${id}-${i}`}
            value={row.lines.join("\n")}
            disabled={disabled}
            minHeight={34}
            onChange={(text) => update(rows.map((r, j) => (j === i ? { ...r, lines: text.split("\n") } : r)))}
            onBlur={onBlur}
          />
        </div>
      ))}
      <p className="px-1 text-meta text-ink-muted">{t("Une ligne par renforcement ou prélèvement.")}</p>
    </div>
  );
}

/** Délai d'enregistrement après la dernière frappe. */
const AUTOSAVE_MS = 800;

/** Éditeur de l'ordre : dix onglets titrés d'après leur contenu, dans l'ordre du PDF.
 *  Enregistrement pendant la frappe (0,8 s après la dernière touche) et en quittant un champ, par la file de
 *  l'ordre (`queue`, orderSaveQueue.ts) ; au passage en arrière-plan et à la fermeture de l'onglet, la saisie en
 *  cours y est versée avant que la file parte en requête non interruptible. Un éditeur ne sert qu'un ordre (dossier
 *  et OPO, `key` posée par la vue) : à son démontage, ce qui reste part dans la file de CET ordre.
 *  Valeur affichée : saisie en cours, sinon valeur non confirmée de la file (jamais remplacée par le texte du
 *  serveur tant qu'elle n'est pas enregistrée), sinon texte de l'ordre. */
export function OrderEditor({
  specs,
  order,
  activeField,
  queue,
  unconfirmed,
  onReset,
  outdated = [],
  frameReview = [],
  onCompare,
  changes,
  onBeforeAfter,
}: {
  specs: OrderFieldSpecs;
  order: Order;
  activeField: string | null;
  queue: OrderSaveQueue;
  unconfirmed: Readonly<Record<string, unknown>>;
  onReset: (field: string) => void;
  /** Paragraphes à revoir (docs/18 §6.7) : modifiés à la main, texte généré changé depuis. */
  outdated?: readonly string[];
  /** Ordre décliné de celui de l'échelon supérieur (docs/20 §13) : paragraphes que l'ordre du chef ne remplit pas,
   *  dont le texte généré est gardé, à revoir. */
  frameReview?: readonly string[];
  /** Ouvre la comparaison « Votre texte » / « Texte généré à jour » d'un paragraphe à revoir. */
  onCompare?: (field: string) => void;
  /** Paragraphes mis à jour d'après le dossier (carte, composition, Données initiales) : teintés, avec « Avant /
   *  après » quand le texte d'avant est gardé. */
  changes?: Readonly<Record<string, OrderChange>>;
  /** Ouvre « Avant / après » d'un champ mis à jour (lecture seule). */
  onBeforeAfter?: (field: string) => void;
}) {
  // Dossier en lecture seule pour ce compte (comptes nominatifs, docs/17 §3.2) : champs figés, comme un ordre finalisé.
  const dossier = useDossierAccess();
  const readOnly = order.status === "finalized" || dossier.readOnly;
  const byKey = useMemo(() => new Map(specs.fields.map((f) => [f.key, f])), [specs.fields]);
  const activeSpec = activeField ? byKey.get(editorKey(activeField)) : undefined;
  const [tab, setTab] = useState<number>(activeSpec?.tab ?? 1);
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});
  const dirty = useRef<Set<string>>(new Set());
  const draftsRef = useRef(drafts);
  const orderRef = useRef(order);
  const timers = useRef<Map<string, number>>(new Map());
  orderRef.current = order;

  useEffect(() => {
    if (activeSpec) setTab(activeSpec.tab);
  }, [activeSpec]);

  // Les brouillons suivent l'ordre enregistré, sauf les champs en cours de frappe et ceux que le serveur n'a pas
  // encore confirmés (un conflit relu, une recomposition par la carte ne remplacent jamais une saisie en attente).
  useEffect(() => {
    setDrafts((d) => {
      const next: Record<string, unknown> = {};
      for (const key of Object.keys(d)) if (dirty.current.has(key) || queue.peek(key).has) next[key] = d[key];
      draftsRef.current = next;
      return next;
    });
  }, [order.version, queue]);

  useEffect(() => {
    if (!activeSpec) return;
    const el = document.getElementById(`field-${activeSpec.key}`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) el.focus({ preventScroll: true });
  }, [activeSpec, tab]);

  const valueOf = (key: string): unknown => {
    if (key in drafts) return drafts[key];
    if (key in unconfirmed) return unconfirmed[key];
    const value = order.fields[key];
    // ME : l'ordre range le texte dans le créneau « défense » ou « attaque » (format de l'iPad) et imprime
    // celui qui est rempli ; l'éditeur montre le texte imprimé et enregistre dans le créneau « défense ».
    const me = /^enemyME([12])DefenseText$/.exec(key);
    if (me && !value) return order.fields[`enemyME${me[1]}AttackText`];
    return value;
  };

  /** Valeur de référence d'un champ : celle qui attend dans la file, sinon celle du serveur. */
  const baseOf = useCallback(
    (key: string): unknown => {
      const queued = queue.peek(key);
      return queued.has ? queued.value : orderRef.current.fields[key];
    },
    [queue],
  );

  /** Champs modifiés et non encore versés dans la file (valeurs les plus récentes). */
  const takeDirty = useCallback((): Record<string, unknown> => {
    const fields: Record<string, unknown> = {};
    for (const key of dirty.current) {
      const value = draftsRef.current[key];
      if (JSON.stringify(value) !== JSON.stringify(baseOf(key))) fields[key] = value;
    }
    dirty.current.clear();
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
    return fields;
  }, [baseOf]);

  const commit = useCallback((key: string) => {
    if (!dirty.current.has(key)) return;
    dirty.current.delete(key);
    const timer = timers.current.get(key);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(key);
    const value = draftsRef.current[key];
    if (JSON.stringify(value) !== JSON.stringify(baseOf(key))) queue.enqueue({ [key]: value });
  }, [baseOf, queue]);

  const set = (key: string, value: unknown) => {
    dirty.current.add(key);
    draftsRef.current = { ...draftsRef.current, [key]: value };
    setDrafts((d) => ({ ...d, [key]: value }));
    const timer = timers.current.get(key);
    if (timer) window.clearTimeout(timer);
    timers.current.set(key, window.setTimeout(() => commit(key), AUTOSAVE_MS));
  };

  // « Rétablir le texte généré » : la saisie en cours et celle qui attend dans la file sont abandonnées, sinon
  // elles écraseraient le texte rétabli.
  const resetField = (key: string) => {
    dirty.current.delete(key);
    const timer = timers.current.get(key);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(key);
    const { [key]: _dropped, ...rest } = draftsRef.current;
    draftsRef.current = rest;
    setDrafts(rest);
    queue.discard(key);
    onReset(key);
  };

  // Arrière-plan et fermeture de l'onglet : la saisie en cours passe dans la file avant son vidage ; démontage
  // (éditeur fermé, autre OPO, autre dossier) : ce qui reste part dans la file de cet ordre.
  useEffect(() => {
    const unregister = registerDrain(() => queue.enqueue(takeDirty(), { send: false }));
    return () => {
      unregister();
      queue.enqueue(takeDirty());
    };
  }, [queue, takeDirty]);

  // Génie : l'ordre imprime la mission offensive ou la mission défensive ; la variante vide n'est pas proposée.
  const unusedVariant = (key: string): boolean => {
    const pair: Record<string, string> = {
      engineerMissionDefensive: "engineerMissionOffensive",
      engineerMissionOffensive: "engineerMissionDefensive",
    };
    const other = pair[key];
    return !!other && !valueOf(key) && !!valueOf(other);
  };
  const fields = specs.fields.filter((f) => f.tab === tab && !unusedVariant(f.key));
  const changed = useMemo(() => changedFields(changes ?? {}), [changes]);

  const renderField = (spec: OrderFieldSpec) => {
    const edited = order.edited.includes(spec.key);
    const stale = edited && outdated.includes(spec.key);
    const framed = !edited && frameReview.includes(spec.key);
    const changeKeys = changed.get(spec.key);
    const comparable = changeKeys && onBeforeAfter && beforeAfter(order, changes ?? {}, changeKeys) !== null;
    const common = { disabled: readOnly };
    return (
      <div
        key={spec.key}
        data-order-field={spec.key}
        data-changed={changeKeys ? "" : undefined}
        className={clsx(
          "rounded-lg px-1 py-1 transition-[background-color,box-shadow] duration-(--gx-enter)",
          spec.key === activeSpec?.key ? "bg-accent-soft/60" : changeKeys && "bg-mark-soft",
          // Trait de révision, comme dans la marge des pages.
          changeKeys && "shadow-[inset_3px_0_0_var(--color-mark)]",
        )}
      >
        <div className="mb-1 flex items-center gap-2">
          <label htmlFor={`field-${spec.key}`} className="min-w-0 flex-1 truncate text-ui font-medium text-ink-soft">
            {term(spec.label)}
          </label>
          {stale ? (
            <Badge dot tone="warn" title={t("Le texte généré a changé depuis votre modification (composition, rôles, Données initiales…)")}>
              {t("À revoir")}
            </Badge>
          ) : framed ? (
            <Badge dot tone="warn" title={t("Texte généré à vérifier : l'ordre de l'échelon supérieur ne le donne pas ou ne lui correspond pas")}>
              {t("À revoir")}
            </Badge>
          ) : (
            edited && <Badge tone="accent">{t("Modifié")}</Badge>
          )}
          {stale && onCompare && (
            <Button variant="link" onClick={() => onCompare(spec.key)}>
              {t("Comparer")}
            </Button>
          )}
          {comparable && (
            <Button
              variant="link"
              onClick={() => onBeforeAfter(spec.key)}
              title={t("Texte d'avant et texte à jour, mis à jour d'après le dossier")}
            >
              {t("Avant / après")}
            </Button>
          )}
          {edited && !readOnly && (
            <IconButton
              size="xs"
              title={stale ? t("Reprendre le texte généré") : t("Rétablir le texte généré")}
              aria-label={stale ? t("Reprendre le texte généré") : t("Rétablir le texte généré")}
              onClick={() => resetField(spec.key)}
            >
              <RotateCcw size={14} />
            </IconButton>
          )}
        </div>
        {/* Rotation CENTAC : d'où vient le texte de ce paragraphe (gabarit, carte, saisies, WINGO, saison ; A117). */}
        {spec.source && <p className="mb-1 text-meta leading-snug text-ink-muted">{t(SOURCE_LABELS[spec.source])}</p>}
        {/* Paragraphe Météo hors CENTAC : délais, annexes, modes d'action et cohérence suivent le réglage du dossier,
            jamais une retouche de ce texte ; le lien mène au réglage. */}
        {spec.key === "weatherText" && !spec.source && (
          <p className="mb-1 flex flex-wrap items-center gap-x-2 text-meta leading-snug text-ink-muted">
            <span>{t("La météo de l'ordre se règle dans les Données initiales : délais, annexes, modes d'action et cohérence en dépendent.")}</span>
            <Button variant="link" onClick={openWeatherSetting}>
              {t("Régler la météo")}
            </Button>
          </p>
        )}
        {spec.kind === "articulation" ? (
          <ArticulationEditor
            id={`field-${spec.key}`}
            value={valueOf(spec.key)}
            onChange={(v) => set(spec.key, v)}
            onBlur={() => setTimeout(() => commit(spec.key), 0)}
            {...common}
          />
        ) : spec.kind === "table" ? (
          <TableEditor
            id={`field-${spec.key}`}
            value={valueOf(spec.key)}
            onChange={(v) => set(spec.key, v)}
            onBlur={() => setTimeout(() => commit(spec.key), 0)}
            {...common}
          />
        ) : spec.kind === "matrix" ? (
          <MatrixEditor
            id={`field-${spec.key}`}
            value={valueOf(spec.key)}
            onChange={(v) => set(spec.key, v)}
            onBlur={() => setTimeout(() => commit(spec.key), 0)}
            {...common}
          />
        ) : spec.kind === "missions" ? (
          <MissionsEditor
            value={(valueOf(spec.key) as UnitMission[] | undefined) ?? []}
            onChange={(v) => set(spec.key, v)}
            onBlur={() => setTimeout(() => commit(spec.key), 0)}
            {...common}
          />
        ) : spec.kind === "line" ? (
          <input
            id={`field-${spec.key}`}
            value={String(valueOf(spec.key) ?? "")}
            onChange={(e) => set(spec.key, e.target.value)}
            onBlur={() => commit(spec.key)}
            className={inputClass}
            {...common}
          />
        ) : (
          <AutoTextarea
            id={`field-${spec.key}`}
            value={String(valueOf(spec.key) ?? "")}
            onChange={(v) => set(spec.key, v)}
            onBlur={() => commit(spec.key)}
            {...common}
          />
        )}
      </div>
    );
  };

  // Paragraphes de l'ordre : une liste déroulante et ses flèches (une rangée de 48 px) plutôt que dix onglets aux
  // titres longs, qui s'empilaient sur six rangées avant le premier champ.
  const tabIndex = specs.tabs.findIndex((tabSpec) => tabSpec.id === tab);
  const framedFields = frameReview.filter((key) => !order.edited.includes(key));
  const staleByTab = outdatedByTab([...new Set([...outdated, ...framedFields])], specs);
  const changedByTab = changesByTab(changes ?? {}, specs);
  const step = (delta: number) => {
    const next = specs.tabs[tabIndex + delta];
    if (next) setTab(next.id);
  };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-line px-3 py-2">
        <IconButton
          size="sm"
          onClick={() => step(-1)}
          disabled={tabIndex <= 0}
          title={t("Paragraphe précédent")}
          aria-label={t("Paragraphe précédent")}
        >
          <ChevronLeft size={16} />
        </IconButton>
        <div className="min-w-0 flex-1">
          <Select className={inputClass} value={String(tab)} onChange={(e) => setTab(Number(e.target.value))}
            aria-label={t("Paragraphes de l'ordre")}>
            {specs.tabs.map((tabSpec) => {
              const stale = staleByTab.get(tabSpec.id) ?? 0;
              const updated = changedByTab.get(tabSpec.id) ?? 0;
              const title = term(tabSpec.title);
              return (
                <option key={tabSpec.id} value={String(tabSpec.id)}>
                  {stale && updated
                    ? t("{title} ({n} à revoir, {m} mis à jour)", { title, n: stale, m: updated })
                    : stale
                      ? t("{title} ({n} à revoir)", { title, n: stale })
                      : updated
                        ? t("{title} ({n} mis à jour)", { title, n: updated })
                        : title}
                </option>
              );
            })}
          </Select>
        </div>
        <IconButton
          size="sm"
          onClick={() => step(1)}
          disabled={tabIndex < 0 || tabIndex >= specs.tabs.length - 1}
          title={t("Paragraphe suivant")}
          aria-label={t("Paragraphe suivant")}
        >
          <ChevronRight size={16} />
        </IconButton>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {fields.map(renderField)}
        {readOnly && (
          <Callout tone="info">
            {dossier.readOnly
              ? readOnlyText(dossier.ownerName)
              : order.kind === "OPLAN"
                ? t("OPLAN validé : rouvrez-le pour le modifier.")
                : t("OPO validé : rouvrez-le pour le modifier.")}
          </Callout>
        )}
      </div>
    </div>
  );
}
